/**
 * Stan asystenta doboru przeniesiony przez wyjście ze strony i powrót.
 *
 * PO CO: użytkownik wypełnia kilkanaście pól, czeka kilkanaście sekund na dobór,
 * wchodzi w wybrany zestaw — i po kliknięciu „wstecz" zastawał pusty formularz.
 * Cała praca do powtórzenia, razem z ponownym czekaniem na silnik.
 *
 * DLACZEGO sessionStorage, a nie stan routera: `navigate(..., { state })` ginie przy
 * odświeżeniu i przy wejściu wstecz z innej karty. Sesja (a nie localStorage) —
 * bo to robocze ustawienia na teraz, a nie preferencja do zapamiętania na stałe.
 *
 * ZAPISUJEMY TEŻ WYNIKI, nie tylko formularz. Same parametry znaczyłyby, że po
 * powrocie trzeba jeszcze raz kliknąć „Dobierz zestawy" i odczekać swoje —
 * a użytkownik cofa się właśnie po to, żeby wrócić do listy, którą już widział.
 */

import { kluczKraju } from '../kraj'

const KLUCZ = kluczKraju('vv:asystent-sesja')

/**
 * Ile czasu wyniki uznajemy za świeże.
 *
 * Ceny i dostępność zmieniają się w ciągu dnia, więc zestaw sprzed tygodnia
 * pokazywałby kwoty, których już nie ma. Godzina to kompromis: obejmuje realną
 * sesję zakupową (przejrzenie zestawu, wejście na strony produktów, powrót),
 * a nie sięga następnego dnia.
 */
const WAZNOSC_MS = 60 * 60 * 1000

/**
 * Zapisujemy TEŻ adres, pod którym sesja powstała.
 *
 * Bez tego nie da się odróżnić dwóch sytuacji, które wyglądają identycznie:
 * ktoś wraca „wstecz" na ten sam ekran (ma zobaczyć swoje wyniki) albo wchodzi
 * świeżym skrótem ze strony głównej z innymi parametrami (ma zobaczyć to, co
 * w skrócie). Pierwsza wersja rozstrzygała to regułą „adres z parametrami wygrywa
 * z sesją" — i wyniki nie wracały NIGDY, bo powrót też ma parametry w adresie.
 */
export const zapiszSesjeAsystenta = (stan, wynik, payload = null, adres = window.location.search) => {
  try {
    sessionStorage.setItem(KLUCZ, JSON.stringify({ stan, wynik, payload, adres, zapisano: Date.now() }))
  } catch {
    // Prywatne okno albo pełny magazyn — asystent ma działać dalej, tylko bez pamięci.
  }
}

export const odczytajSesjeAsystenta = () => {
  try {
    const surowe = sessionStorage.getItem(KLUCZ)
    if (!surowe) return null
    const dane = JSON.parse(surowe)
    if (!dane?.stan) return null
    if (!dane.zapisano || Date.now() - dane.zapisano > WAZNOSC_MS) return null
    return dane
  } catch {
    return null
  }
}

export const wyczyscSesjeAsystenta = () => {
  try { sessionStorage.removeItem(KLUCZ) } catch { /* nic nie szkodzi */ }
  try { sessionStorage.removeItem(KLUCZ_NAZWY) } catch { /* nic nie szkodzi */ }
}

/**
 * Nazwa zestawu wpisana na starcie doboru (np. nazwisko klienta).
 *
 * OSOBNY KLUCZ, a nie pole w stanie formularza: nazwa nie jest parametrem
 * doboru — nie idzie do silnika, nie zmienia wyniku i nie ma jej w adresie.
 * Wciśnięta do stanu reduktora jechałaby przez całą logikę porównywania
 * parametrów i „zmiana nazwy" wyglądałaby jak zmiana kryteriów, po której
 * trzeba przeliczyć dobór od nowa.
 *
 * Ta sama sesja, ta sama ważność co reszta: to notatka na teraz, nie preferencja.
 */
const KLUCZ_NAZWY = kluczKraju('vv:asystent-nazwa')

export const zapiszNazweZestawu = (nazwa) => {
  try {
    if (nazwa) sessionStorage.setItem(KLUCZ_NAZWY, nazwa)
    else sessionStorage.removeItem(KLUCZ_NAZWY)
  } catch { /* prywatne okno — asystent działa dalej, tylko bez pamięci */ }
}

export const odczytajNazweZestawu = () => {
  try { return sessionStorage.getItem(KLUCZ_NAZWY) || '' } catch { return '' }
}
